"use client";

import { Bookmark, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useCartStore } from "@/components/providers/cart-store";
import { useWishlistStore } from "@/components/providers/wishlist-store";
import { calculateShipping } from "@/lib/delivery";
import { formatPrice } from "@/lib/utils";

export function CartView() {
  const [mounted, setMounted] = useState(false);
  const items = useCartStore((s) => s.items);
  const savedItems = useCartStore((s) => s.savedItems);
  const subtotal = useCartStore((s) => s.subtotal());
  const couponDiscount = useCartStore((s) => s.couponDiscount);
  const applyCouponStore = useCartStore((s) => s.applyCoupon);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const saveForLater = useCartStore((s) => s.saveForLater);
  const moveToCart = useCartStore((s) => s.moveToCart);
  const moveToWishlist = useCartStore((s) => s.moveToWishlist);
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => setMounted(true), []);

  const shipping = calculateShipping(subtotal, true);
  const total = subtotal - couponDiscount + shipping;

  async function handleCoupon() {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    try {
      const response = await fetch("/api/coupons/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponInput.trim(), subtotal })
      });
      const result = await response.json();
      if (result.valid && result.discount > 0) {
        applyCouponStore(couponInput.toUpperCase(), result.discount);
        toast.success(result.message);
      } else {
        toast.error(result.message ?? "Invalid coupon code");
      }
    } catch {
      toast.error("Unable to validate coupon");
    } finally {
      setCouponLoading(false);
    }
  }

  if (!mounted) {
    return (
      <div className="amazon-section py-16 text-center">
        <div className="mx-auto h-8 w-48 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      </div>
    );
  }

  if (items.length === 0 && savedItems.length === 0) {
    return (
      <div className="amazon-section py-16 text-center">
        <ShoppingBag className="mx-auto h-14 w-14 text-amazon-orange" />
        <h1 className="mt-5 text-3xl font-bold">Your MOVA Cart is empty</h1>
        <Link href="/search" className="amazon-btn-primary mt-6 inline-flex">
          Continue shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] grid gap-4 px-3 py-4 sm:px-4 lg:grid-cols-[1fr_320px]">
      <section className="rounded border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:p-5">
        <div className="flex items-baseline justify-between border-b border-slate-200 pb-3 dark:border-white/10">
          <h1 className="text-2xl font-medium text-slate-950 dark:text-white">Shopping Cart</h1>
          <span className="hidden text-sm text-slate-500 sm:inline">Price</span>
        </div>

        <div className="divide-y divide-slate-200 dark:divide-white/10">
          {items.map(({ product, quantity, selectedVariant }) => {
            const itemKey = `${product.id}-${selectedVariant?.id || "base"}`;
            const itemImage = selectedVariant?.image || product.images[0];
            const itemUnitPrice = selectedVariant?.price ?? product.price;

            return (
              <article key={itemKey} className="flex flex-col gap-4 py-5 sm:flex-row sm:justify-between">
                <div className="flex gap-3 sm:gap-4">
                  <Link href={`/products/${product.slug}`} className="relative h-20 w-20 sm:h-28 sm:w-28 shrink-0 overflow-hidden rounded border border-slate-100 bg-white dark:border-white/5">
                    <Image src={itemImage} alt={product.title} fill sizes="(max-width: 640px) 80px, 112px" className="object-contain p-1" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link href={`/products/${product.slug}`} className="amazon-link line-clamp-2 font-medium">
                      {product.title}
                    </Link>
                    {selectedVariant && (
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                        <span className="font-semibold text-slate-800 dark:text-slate-200">Color:</span>
                        <span
                          className="inline-block h-3 w-3 rounded-full border border-black/20"
                          style={{ backgroundColor: selectedVariant.colorCode || "#333" }}
                        />
                        <span>{selectedVariant.color}</span>
                        <span className="text-slate-400">({formatPrice(selectedVariant.price)} each)</span>
                      </div>
                    )}
                    <p className="mt-1 text-xs text-amazon-green">In stock</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <div className="inline-flex h-8 items-center rounded border border-slate-300 dark:border-white/10">
                        <button
                          type="button"
                          onClick={() => updateQuantity(product.id, quantity - 1, selectedVariant?.id)}
                          className="px-2.5 hover:bg-slate-50 dark:hover:bg-white/10"
                          aria-label="Decrease"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="min-w-8 text-center text-sm font-bold">{quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(product.id, quantity + 1, selectedVariant?.id)}
                          className="px-2.5 hover:bg-slate-50 dark:hover:bg-white/10"
                          aria-label="Increase"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => saveForLater(product.id, selectedVariant?.id)}
                        className="text-xs text-amazon-teal hover:underline"
                      >
                        Save for later
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          toggleWishlist(product);
                          moveToWishlist(product.id, selectedVariant?.id);
                          toast.success("Moved to wishlist");
                        }}
                        className="text-xs text-amazon-teal hover:underline"
                      >
                        Move to wishlist
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(product.id, selectedVariant?.id)}
                        className="inline-flex items-center gap-1 text-xs text-amazon-teal hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                <div className="text-right text-lg font-bold">{formatPrice(itemUnitPrice * quantity)}</div>
              </article>
            );
          })}
        </div>

        {savedItems.length > 0 && (
          <div className="mt-8 border-t pt-6">
            <h2 className="flex items-center gap-2 font-bold">
              <Bookmark className="h-4 w-4" />
              Saved for later ({savedItems.length})
            </h2>
            {savedItems.map(({ product, quantity }) => (
              <div key={product.id} className="mt-4 flex gap-4">
                <div className="relative h-20 w-20 shrink-0 bg-slate-50">
                  <Image src={product.images[0]} alt="" fill sizes="80px" className="object-contain p-1" />
                </div>
                <div className="flex-1">
                  <p className="line-clamp-2 text-sm">{product.title}</p>
                  <button type="button" onClick={() => moveToCart(product.id)} className="mt-2 text-xs text-amazon-teal hover:underline">
                    Move to cart
                  </button>
                </div>
                <span className="font-bold">{formatPrice(product.price * quantity)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <aside className="h-fit rounded border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 lg:sticky lg:top-20">
        <h2 className="text-lg">
          Subtotal ({items.reduce((n, i) => n + i.quantity, 0)} items):{" "}
          <span className="font-bold text-slate-950 dark:text-white">{formatPrice(subtotal)}</span>
        </h2>
        <div className="mt-4 flex gap-2">
          <input
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            placeholder="Enter coupon"
            className="h-10 flex-1 min-w-0 rounded border px-3 text-sm dark:border-white/10 dark:bg-slate-950"
          />
          <button onClick={handleCoupon} disabled={couponLoading} className="inline-flex h-10 shrink-0 items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-bold transition hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:bg-slate-900">{couponLoading ? "…" : "Apply"}</button>
        </div>
        <div className="mt-4 space-y-2 text-sm">
          {couponDiscount > 0 && (
            <div className="flex justify-between text-amazon-green">
              <span>Coupon</span>
              <strong>-{formatPrice(couponDiscount)}</strong>
            </div>
          )}
          <div className="flex justify-between">
            <span>Shipping</span>
            <strong>{shipping === 0 ? "FREE" : formatPrice(shipping)}</strong>
          </div>
          <div className="border-t pt-2 text-base font-bold">
            <div className="flex justify-between">
              <span>Order total</span>
              <span className="text-amazon-red">{formatPrice(total)}</span>
            </div>
          </div>
        </div>
        <Link href="/checkout" className="amazon-btn-primary mt-5 flex h-11 w-full items-center justify-center">
          Proceed to checkout
        </Link>
      </aside>
    </div>
  );
}
